import { toDateLike } from "@racoon/lib";
import { ConnectProto } from "@racoon/api";
import type { TravelPathResponse } from "~/shared/api/truck/truck.grpc";
import { normalizeGpsData } from "./gps-normalization";

/**
 * Lookback used when a route carries neither fact nor gps timestamps: routes
 * that are only planned (or scheduled for later) still need the truck's current
 * whereabouts, so we ask for the last few minutes of its track.
 */
export const PLANNED_ROUTE_LOOKBACK_MS = 10 * 60 * 1000;

/** `openStart` / `openEnded`: that side has no recorded bound, so it was derived
 *  from the moment the window was built. */
export type TravelPathWindow = {
  from: Date;
  to: Date;
  openStart: boolean;
  openEnded: boolean;
};

/**
 * Time window to request the truck's GPS track for a route:
 * from = min(fact_start_at, gps_start_at), falling back to now - 10 minutes,
 * to   = max(fact_end_at, gps_end_at), falling back to now.
 */
export function getTravelPathWindow(
  route: ConnectProto.Route,
  now: Date = new Date(),
): TravelPathWindow {
  const starts = [toDateLike(route.factStartAt), toDateLike(route.gpsStartAt)].filter(
    (date): date is Date => date != null,
  );
  const ends = [toDateLike(route.factEndAt), toDateLike(route.gpsEndAt)].filter(
    (date): date is Date => date != null,
  );

  const from = starts.length
    ? new Date(Math.min(...starts.map((date) => date.getTime())))
    : new Date(now.getTime() - PLANNED_ROUTE_LOOKBACK_MS);

  const to = ends.length ? new Date(Math.max(...ends.map((date) => date.getTime()))) : now;

  // A route can carry an end without any start, and the lookback fallback can
  // land after an already recorded end; an inverted window would come back empty.
  return {
    from,
    to: to.getTime() < from.getTime() ? from : to,
    openStart: starts.length === 0,
    openEnded: ends.length === 0,
  };
}

export type TravelPathPoint = { lat: number; lon: number; angle?: number };

/**
 * Latest known position of the truck within a travel path, used to place the
 * truck marker on the track instead of the route's planned source point.
 */
export function getLatestTravelPathPoint(travelPath?: TravelPathResponse): TravelPathPoint | null {
  const coordinates = travelPath?.geometry?.coordinates;
  if (!coordinates?.length) return null;

  const properties = travelPath?.properties;
  const rawPoints = coordinates
    .map((coord, index) => ({
      lat: coord[1],
      lon: coord[0],
      timestamp: properties?.timestamps?.[index] || 0,
      angle: properties?.angles?.[index],
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon));

  if (!rawPoints.length) return null;

  // Same normalization the black GPS track uses, so the marker lands exactly on
  // the end of the drawn track rather than on a jitter point beside it.
  const normalized = normalizeGpsData(rawPoints);
  const latest = normalized[normalized.length - 1] ?? rawPoints[rawPoints.length - 1];

  return { lat: latest.lat, lon: latest.lon, angle: latest.angle };
}
